import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTriangleExclamation,
  faSpinner,
  faWallet,
  faEnvelope,
  faChevronLeft,
  faChevronRight,
  faShieldAlt,
} from "@fortawesome/free-solid-svg-icons";
import { Helmet } from "react-helmet";
import "../scss/index.scss";
import { getOwnWalletAudit } from "../store/actions";
import TwoFactorSection from "../components/globalView/TwoFactorSection";
import AgencyAccessSection from "../components/globalView/AgencyAccessSection";
import LoginEmailSection from "../components/globalView/LoginEmailSection";

const shortHash = (addr) => {
  if (!addr || typeof addr !== "string") return "∅";
  if (addr.length <= 14) return addr;
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
};

const sourceLabel = (changedBy) => {
  const src = (changedBy || "").toLowerCase();
  if (src.includes("trade")) return "trade app";
  if (src.includes("cms") || src.includes("partner")) return "partner dashboard";
  if (src.includes("admin")) return "admin";
  if (src.includes("backfill") || src.includes("auto")) return "auto-backfill";
  return changedBy || "unknown";
};

const buildReportMailto = (row, username) => {
  const subject = encodeURIComponent(
    `[Security] Unauthorized change report — audit #${row.id}`
  );
  const body = encodeURIComponent(
    `Hello Instaswap Support,\n\n` +
      `I am reporting the following change as unauthorized.\n\n` +
      `Audit ID: ${row.id}\n` +
      `Field: ${row.field_changed}\n` +
      `Old value: ${row.old_value || "(empty)"}\n` +
      `New value: ${row.new_value || "(empty)"}\n` +
      `When: ${row.changed_at}\n` +
      `Source: ${row.changed_by || "unknown"}\n` +
      `IP: ${row.caller_ip || "unknown"}\n\n` +
      `My partner account: ${username || "(unknown)"}\n\n` +
      `Please investigate.\n`
  );
  return `mailto:support@instaswap.com?subject=${subject}&body=${body}`;
};

const Security = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const alertId = searchParams.get("alertId");

  const ITEMS_PER_PAGE = 25;
  const [page, setPage] = useState(1);

  const walletAudit = useSelector((state) => state.userReducer.walletAudit);
  const loading = useSelector((state) => state.userReducer.walletAuditLoading);
  const user = useSelector((state) => state.userReducer.user);
  const username = user?.response?.email || user?.response?.partnerName;

  const rows = Array.isArray(walletAudit)
    ? walletAudit
    : walletAudit?.items || [];
  const total = Array.isArray(walletAudit)
    ? walletAudit.length
    : Number(walletAudit?.total) || 0;
  const totalPages = Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));

  const highlightedRef = useRef(null);

  useEffect(() => {
    dispatch(getOwnWalletAudit({ page, itemsPerPage: ITEMS_PER_PAGE }));
  }, [dispatch, page]);

  // Scroll highlighted row into view when data arrives
  useEffect(() => {
    if (!alertId || !rows || rows.length === 0) return;
    // Defer to next tick so the row has rendered
    const t = setTimeout(() => {
      if (highlightedRef.current) {
        highlightedRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 100);
    return () => clearTimeout(t);
  }, [alertId, rows]);

  const sortedRows = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    return [...rows].sort((a, b) => {
      const ta = new Date(a.changed_at).getTime() || 0;
      const tb = new Date(b.changed_at).getTime() || 0;
      return tb - ta;
    });
  }, [rows]);

  return (
    <>
      <Helmet>
        <title>Security | Instaswap Partners</title>
      </Helmet>

      <div>
        <div
          className="secHdr"
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 18,
            flexWrap: "wrap",
          }}
        >
          <div>
            <h2 className="secTitle">Security</h2>
            <p className="secLead">
              Review recent security events on your partner account. If you see
              anything you did not authorize, report it immediately.
            </p>
          </div>
          <Link to="/linked-wallets" className="btnGhost sm" style={{ flex: "none" }}>
            <FontAwesomeIcon icon={faWallet} /> Linked wallets
          </Link>
        </div>

        <LoginEmailSection />

        <TwoFactorSection />

        <AgencyAccessSection />

        <div style={{ maxWidth: 900 }}>
          <h3 className="secH" style={{ marginTop: 0 }}>
            Recent security events
          </h3>

          {loading ? (
            <div style={{ padding: 20, color: "#a7b2ab" }}>
              <FontAwesomeIcon icon={faSpinner} spin /> Loading events…
            </div>
          ) : sortedRows.length === 0 ? (
            <div className="gtScroll">
              <div className="gtEmpty">
                No security events recorded for your account.
              </div>
            </div>
          ) : (
            <div className="scList">
              {sortedRows.map((row) => {
                const isHighlighted =
                  alertId && String(row.id) === String(alertId);
                return (
                  <div
                    key={row.id}
                    ref={isHighlighted ? highlightedRef : null}
                    className="scEvent"
                    style={
                      isHighlighted
                        ? {
                            background: "rgba(230,192,106,.07)",
                            boxShadow: "inset 3px 0 0 #e6c06a",
                          }
                        : undefined
                    }
                  >
                    <span className={`scEvIco ${isHighlighted ? "warn" : "ok"}`}>
                      <FontAwesomeIcon icon={faShieldAlt} />
                    </span>
                    <div className="scEvBody">
                      <div className="scEvT" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}>
                        <strong>{row.field_changed}</strong>:{" "}
                        {shortHash(row.old_value)} → {shortHash(row.new_value)}
                      </div>
                      <div className="scEvM">
                        source: <strong style={{ color: "#cdd5cf" }}>{sourceLabel(row.changed_by)}</strong>
                        {row.caller_login_method
                          ? ` (${row.caller_login_method})`
                          : ""}
                        <span className="dot">·</span>
                        ip: <code>{row.caller_ip || "unknown"}</code>
                        {row.email_sent ? (
                          <>
                            <span className="dot">·</span>
                            <FontAwesomeIcon
                              icon={faEnvelope}
                              title="Notification email was sent"
                            />
                          </>
                        ) : null}
                      </div>
                      {row.notes && (
                        <div className="scEvM" style={{ marginTop: 4 }}>
                          {row.notes}
                        </div>
                      )}
                      <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {isHighlighted && (
                          <span
                            className="pill pend"
                            style={{ fontWeight: 700 }}
                          >
                            LINKED FROM EMAIL
                          </span>
                        )}
                        <a
                          href={buildReportMailto(row, username)}
                          className="btnGhost sm danger"
                          style={{ color: "#ff9d94", borderColor: "rgba(255,122,110,.35)" }}
                        >
                          <FontAwesomeIcon icon={faTriangleExclamation} />
                          Report as unauthorized
                        </a>
                      </div>
                    </div>
                    <div className="scEvTime">
                      <div className="d">{row.changed_at}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {totalPages > 1 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 14,
                marginTop: 20,
              }}
            >
              <button
                type="button"
                className="btnGhost sm"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <FontAwesomeIcon icon={faChevronLeft} /> Prev
              </button>
              <span style={{ fontSize: 13, color: "#7c847e" }}>
                Page {page} of {totalPages} · {total} events
              </span>
              <button
                type="button"
                className="btnGhost sm"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          )}

          <div className="scFoot">
            <FontAwesomeIcon icon={faTriangleExclamation} className="ni" />
            Don't recognize an event? Relink your login wallet and contact
            support immediately.
          </div>
        </div>
      </div>
    </>
  );
};

export default Security;
